import { Injectable, OnDestroy } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { CorrelationIdGenerator } from "@ngrx/data";
import {
	Basics,
	distinctUntilJsonChanged,
	filterUndefined,
	Member,
	SessionService,
} from "@recore-solutions/nstar-lib";
import {
	BehaviorSubject,
	combineLatest,
	merge,
	Observable,
	PartialObserver,
	Subject,
	Subscription,
} from "rxjs";
import {
	debounceTime,
	delay,
	distinctUntilChanged,
	distinctUntilKeyChanged,
	filter,
	map,
	pairwise,
	shareReplay,
	startWith,
	switchMap,
	take,
	tap,
	withLatestFrom,
} from "rxjs/operators";
import { ConfigurationService } from "../configuration.service";
import { PreferencesService } from "../preferences/preferences.service";
import * as CacheBusters from "../shared/cache-busters";
import { convertLegacyToPolarisSortColumn } from "../shared/components/listing-sort-select/listing-sort-columns";
import { ListingSummary } from "../shared/interfaces/listing-summary";
import { ListingSummaryRequest } from "../shared/interfaces/listing-summary-request";
import { Preferences } from "../shared/interfaces/preferences";
import {
	listingSummaryFilterFn,
	listingSummarySortFn,
} from "../shared/metadata/listing-summary.metadata";
import { ListingSummaryService } from "../shared/services/listing-summary.service";
import { MinimumArrayLengthValidator } from "../shared/validators";
import { whenToSearchFilter } from "../shared/when-to-search-filter";
import { ListingSortColumn } from "./types/ListingSortColumn";

@Injectable({
	providedIn: "root",
})
export class HomeListingResultsService implements OnDestroy {
	listingSummaryRequestForm: UntypedFormGroup;
	pageSizeControl: UntypedFormControl = new UntypedFormControl("10");

	listingSummary$: Observable<ListingSummary[]>;
	listingSummaryCount$: Observable<number> = this.listingSummaryService.filteredEntities$.pipe(
		map((summary) => summary.length),
		distinctUntilChanged()
	);

	cardOrRowSubject: Subject<"card" | "row"> = new Subject();
	cardOrRow$: Observable<"card" | "row">;

	private subscriptions: Subscription[] = [];

	private searchNumberSubject: BehaviorSubject<number> = new BehaviorSubject(0);
	searchNumber$: Observable<number> = this.searchNumberSubject.asObservable();

	inProgressFn: () => void = () => {};
	finishedFn: () => void = () => {};

	private currentPageSubject: BehaviorSubject<number> = new BehaviorSubject<number>(1);
	currentPage$: Observable<number>;
	get currentPage(): number {
		return this.currentPageSubject.value;
	}
	set currentPage(page: number) {
		this.currentPageSubject.next(page);
	}
	currentCorrelationId: string;
	previousCorrelationId: string;

	originalFilter: ListingSummary[] = [];
	previousFilter: ListingSummary[] = [];
	previousListingSummaryRequest: ListingSummaryRequest;

	get maxSearchResults(): number {
		return this.configService?.config?.listings?.maxSearchResults || 1000;
	}

	constructor(
		public listingSummaryService: ListingSummaryService,
		public sessionService: SessionService,
		public preferencesService: PreferencesService,
		public correlationIdGenerator: CorrelationIdGenerator,
		public configService: ConfigurationService
	) {
		this.listingSummaryRequestForm = new UntypedFormGroup({
			listingStatusCD: new UntypedFormControl(["ACT"], MinimumArrayLengthValidator(1)),
			propertyTypeCD: new UntypedFormControl(["SF"], MinimumArrayLengthValidator(1)),
			sortColumn: new UntypedFormControl(ListingSortColumn.MODIFICATION),
			isSortASC: new UntypedFormControl(false),
			when: new UntypedFormControl("30:days::0:days"),
			search: new UntypedFormControl([]),
		});
		this.subscriptions.push(
			this.preferencesService.preference$
				.pipe(
					take(1),
					map(this.mapPreferencesToUpdatedSearchCriteria(this.listingSummaryRequestForm.value)),
					delay(0),
					tap(this.listingSummaryRequestForm.patchValue.bind(this.listingSummaryRequestForm)),
					switchMap(() => this.listingSummaryRequestForm.get("sortColumn").valueChanges),
					startWith(this.listingSummaryRequestForm.get("sortColumn").value),
					pairwise(),
					filter((values) => this.everyElementIsEqual(values)),
					map(() => this.listingSummaryRequestForm.get("isSortASC")),
					tap((control) => this.invertControlValue(control))
				)
				.subscribe()
		);
		const resetPageOnChange: string[] = ["listingStatusCD", "propertyTypeCD", "search", "when"];
		this.subscriptions.push(
			merge(
				...resetPageOnChange.map((name) => this.listingSummaryRequestForm.get(name).valueChanges)
			)
				.pipe(
					map(() => 1), // If any of those fields change, map it to a 1
					tap(this.currentPageSubject.next.bind(this.currentPageSubject)) // and set the current page to that value.
				)
				.subscribe()
		);
		const bulkListingsQueryFormValues = this.listingSummaryRequestForm.valueChanges.pipe(
			debounceTime(500),
			distinctUntilJsonChanged(),
			tap(() => this.cancelPreviousRequest()), // Before the startWith because we never need to cancel the first request.
			startWith(this.listingSummaryRequestForm.value), // Make a request with the initial values.
			pairwise(), // Some changes can be handled locally, but we need to see chaneges to determine that.
			withLatestFrom(this.listingSummaryCount$), // Local changes can only be handled if the current count is less than the MAX.
			map(([[_oldRequest, newRequest], _count]) => newRequest),
			tap((listingSummaryRequest) => this.setPreviousListingSummaryRequest(listingSummaryRequest)), // Cache the request for reference later.
			shareReplay({ bufferSize: 1, refCount: true }) // Any additional subscribers should get the same results.
		);
		const memberBasic$ = this.sessionService.memberBasic$.pipe(
			filterUndefined(),
			distinctUntilKeyChanged("memberReference"),
			tap(() => this.clearCache())
		);
		const bulkListingUpdateTrigger: Observable<[any, Basics<Member>]> = combineLatest([
			bulkListingsQueryFormValues,
			memberBasic$,
		]);
		this.listingSummary$ = bulkListingUpdateTrigger.pipe(
			tap(() => this.incrementSearchNumber()),
			filter(() => this.listingSummaryRequestFormIsValid()),
			tap(() => this.inProgressFn()),
			tap(() => {
				this.previousCorrelationId = this.currentCorrelationId
					? this.currentCorrelationId
					: undefined;
			}),
			switchMap((listingSummaryRequest: [ListingSummaryRequest, Basics<Member>]) =>
				this.searchListingSummary(listingSummaryRequest)
			),
			tap(() => this.finishedFn()),
			switchMap(() => this.listingSummaryService.filteredEntities$),
			shareReplay({ bufferSize: 1, refCount: false })
		);
		this.cardOrRow$ = merge(
			this.cardOrRowSubject.asObservable(),
			this.preferencesService.getPreference(["homepage", "listingDisplayView"])
		).pipe(
			map((view) => view || "grid"),
			shareReplay({ bufferSize: 1, refCount: true })
		);
		this.currentPage$ = combineLatest([
			this.currentPageSubject.asObservable().pipe(distinctUntilChanged()),
			this.listingSummaryCount$,
			this.pageSizeControl.valueChanges.pipe(
				startWith(this.pageSizeControl.value),
				distinctUntilChanged()
			),
		]).pipe(
			map(([page, count, pageSize]) => this.calculateCurrentPage([page, count, pageSize])),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	calculateCurrentPage = ([page, count, pageSize]: [number, number, number | string]) => {
		pageSize = +pageSize;
		// The current page is out of bounds. Set correctly.
		if (page > 1 && count > 0 && (page - 1) * pageSize >= count) {
			page = Math.ceil(count / pageSize);
			this.currentPage = page;
		} else if (page < 1) {
			this.currentPage = page;
		}
		return page;
	};

	everyElementIsEqual = ([val, ...els]: any[]): boolean => {
		return els.every((el) => el === val);
	};

	invertControlValue = (control: UntypedFormControl): void => {
		control?.setValue(!control.value);
	};

	incrementSearchNumber = (): void => {
		this.searchNumberSubject.next(this.searchNumberSubject.value + 1);
	};

	listingSummaryRequestFormIsValid = (): boolean => {
		return this.listingSummaryRequestForm?.status === "VALID";
	};

	searchListingSummary = ([listingSummaryRequest]: [ListingSummaryRequest, Basics<Member>]) => {
		const when = whenToSearchFilter(listingSummaryRequest.when);
		const startDate = when.find((f) => f.type === "StartDate")?.value || "1900-01-01 12:00:00";
		const endDate = when.find((f) => f.type === "EndDate")?.value || null;
		const pageNumber = String(1);
		const pageSize = String(this.maxSearchResults);
		const sortColumn =
			!!listingSummaryRequest?.sortColumn && !Array.isArray(listingSummaryRequest.sortColumn)
				? listingSummaryRequest.sortColumn
				: ListingSortColumn.MODIFICATION;
		const sortDirection = listingSummaryRequest?.isSortASC ? "ASC" : "DESC";
		const sortString =
			convertLegacyToPolarisSortColumn(sortColumn).split(",").join(` ${sortDirection}, `) +
			" " +
			sortDirection;
		let propertyTypeCDs = "Type=" + (listingSummaryRequest?.propertyTypeCD.sort() || []).join(",");
		if (propertyTypeCDs === "Type=CMU,FRM,FRMRLIT,LL,LLRLIT,MF,RR,SF") {
			// If we are searching all property types, it's quicker just to not filter by property type at all.
			propertyTypeCDs = "";
		}
		let listingStatusCDs =
			"Status=" + (listingSummaryRequest?.listingStatusCD.sort() || []).join(",");
		if (
			listingStatusCDs ===
			"Status=ACT,CANCL,CLOSD,COMPS,CSN,EXP,I,INC,ID,WD,D,OAA,OAS,OAW,OACS,PEND,RENTD,TNAFS,W,WE"
		) {
			// If we are using "all" statuses, then just exclude duplicate listings.
			listingStatusCDs = "Status=!DUP";
		}
		const search = (listingSummaryRequest?.search || []).map((f) => [f.type, f.value].join("="));
		const timeframe = "StartDate=" + startDate + "&EndDate=" + endDate;
		const queryString = [timeframe, propertyTypeCDs, listingStatusCDs, ...search]
			.filter((p) => !!p)
			.join("&");

		this.currentCorrelationId = this.correlationIdGenerator.next();
		// Clear current filter.
		this.setListingSummaryFilter([]);
		return this.listingSummaryService
			.getWithQuery(
				{
					startDate,
					pageNumber,
					pageSize,
					sortString,
					queryString,
				},
				{ tag: "ListingSummarySearch", correlationId: this.currentCorrelationId }
			)
			.pipe(
				tap((listingSummaries: ListingSummary[]) => this.setOriginalFilter(listingSummaries)),
				tap((listingSummaries: ListingSummary[]) => this.setListingSummaryFilter(listingSummaries))
			);
	};

	cancelPreviousRequest = (): void => {
		if (!!this.previousCorrelationId && this.previousCorrelationId !== this.currentCorrelationId) {
			this.listingSummaryService.cancel(this.previousCorrelationId);
		}
	};

	setOriginalFilter = (listingSummaries: ListingSummary[]): void => {
		this.originalFilter = listingSummaries;
	};

	setListingSummaryFilter = (listingSummaries: ListingSummary[]): void => {
		this.previousFilter = listingSummaries;
		this.listingSummaryService.setFilter(
			this.listingSummaryFilterAndSortFn(listingSummaries.map((ls) => ls.listingID))
		);
	};

	listingSummaryFilterAndSortFn = (listingID: ListingSummary["listingID"][]) => {
		const listingStatusCD: string[] =
			this.listingSummaryRequestForm.value?.listingStatusCD.join(",").split(",") || [];
		const propertyTypeCD: string[] =
			this.listingSummaryRequestForm.value?.propertyTypeCD.join(",").split(",") || [];
		const filterFn = listingSummaryFilterFn({ listingID, listingStatusCD, propertyTypeCD });
		const sortFn = listingSummarySortFn(listingID);
		return (listingSummaries: ListingSummary[]): ListingSummary[] => {
			return listingSummaries.filter(filterFn).sort(sortFn);
		};
	};

	selectListingSummarySearchResults = (): Observable<ListingSummary[]> => {
		return this.listingSummaryService.filteredEntities$;
	};

	refresh(): void {
		CacheBusters.listingCacheBuster$.next();
	}

	clearCache = (): void => {
		this.setListingSummaryFilter([]);
		this.listingSummaryService.clearCache();
	};

	mapPreferencesToUpdatedSearchCriteria =
		(currentSearchCriteria: ListingSummaryRequest) =>
		({ homepage }: Preferences) => {
			this.pageSizeControl.setValue(homepage?.pageSize || this.pageSizeControl.value);
			return {
				listingStatusCD:
					homepage?.listingStatus?.length > 0
						? homepage?.listingStatus
						: currentSearchCriteria.listingStatusCD,
				propertyTypeCD:
					homepage?.listingType?.length > 0
						? homepage?.listingType
						: currentSearchCriteria.propertyTypeCD,
				sortColumn:
					homepage?.listingSortBy?.length > 0
						? homepage.listingSortBy
						: currentSearchCriteria.sortColumn,
				isSortASC: homepage?.listingSortDirection || currentSearchCriteria.isSortASC,
				when:
					homepage?.listingTimeframe?.length > 0
						? homepage.listingTimeframe
						: currentSearchCriteria.when,
				search: homepage?.listingSearch || currentSearchCriteria.search,
			};
		};

	subscribeSafely = (obs: Observable<any>, observer?: PartialObserver<any>): void => {
		this.subscriptions.push(obs.subscribe(observer));
	};

	ngOnDestroy(): void {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}

	updateSortAndFilterLocallyIfPossible = ([[before, after], count]: [
		[ListingSummaryRequest, ListingSummaryRequest],
		number
	]) => {
		// If there are fewer than MAX records we can try to re-sort/re-filter locally.
		// If there are MAX records or more, we can't know if other records should now be included.
		if (count > 0 && count < this.maxSearchResults) {
			if (before.isSortASC !== after.isSortASC) {
				this.setListingSummaryFilter(this.previousFilter.reverse());
				return false;
			}

			if (before.listingStatusCD?.length !== after.listingStatusCD?.length) {
				if (
					after.listingStatusCD.every((cd) =>
						this.previousListingSummaryRequest?.listingStatusCD.includes(cd)
					)
				) {
					this.setListingSummaryFilter(this.previousFilter);
					return false;
				}
			}
			if (before.propertyTypeCD?.length !== after.propertyTypeCD?.length) {
				if (
					after.propertyTypeCD.every((cd) =>
						this.previousListingSummaryRequest?.propertyTypeCD.includes(cd)
					)
				) {
					this.setListingSummaryFilter(this.previousFilter);
					return false;
				}
			}

			// if the sort column itself has changed, re-sort if possible.
			if (before.sortColumn !== after.sortColumn) {
				if (after.sortColumn === ListingSortColumn.MLID) {
					this.setListingSummaryFilter(
						this.previousFilter.sort(this.sortListingSummaryByProperty("listingID"))
					);
					return false;
				}
				if (after.sortColumn === ListingSortColumn.MEMBER_NAME) {
					this.setListingSummaryFilter(
						this.previousFilter.sort(this.sortListingSummaryByProperty("listingMemberSortName"))
					);
					return false;
				}
				if (after.sortColumn === ListingSortColumn.STATUS_NAME) {
					this.setListingSummaryFilter(
						this.previousFilter.sort(this.sortListingSummaryByProperty("listingStatusCD"))
					);
					return false;
				}
				if (after.sortColumn === ListingSortColumn.CITY_NAME) {
					this.setListingSummaryFilter(
						this.previousFilter.sort(this.sortListingSummaryByProperty("cityName"))
					);
					return false;
				}
				if (after.sortColumn === ListingSortColumn.PRICE) {
					this.setListingSummaryFilter(
						this.previousFilter.sort(this.sortListingSummaryByProperty("price"))
					);
					return false;
				}
				if (after.sortColumn === ListingSortColumn.DATE) {
					this.setListingSummaryFilter(
						this.previousFilter.sort(this.sortListingSummaryByProperty("listingContractDT"))
					);
					return false;
				}
				if (after.sortColumn === ListingSortColumn.EXPIRATION) {
					this.setListingSummaryFilter(
						this.previousFilter.sort(this.sortListingSummaryByProperty("expirationDT"))
					);
					return false;
				}
				if (after.sortColumn === ListingSortColumn.MODIFICATION) {
					this.setListingSummaryFilter(
						this.previousFilter.sort(
							this.sortListingSummaryByProperty("modificationTimestampUTCDTTM")
						)
					);
					return false;
				}
			}
		}
		return true;
	};

	setPreviousListingSummaryRequest = (listingSummaryRequest: ListingSummaryRequest): void => {
		this.previousListingSummaryRequest = listingSummaryRequest;
	};

	// In some situations we can re-sort locally.
	sortListingSummaryByProperty = (
		prop: keyof ListingSummary | ((s: ListingSummary) => any),
		isSortASC: boolean = false
	) => {
		isSortASC = this.listingSummaryRequestForm.value.isSortASC;
		return (a: ListingSummary, b: ListingSummary): number => {
			const aValue = typeof prop === "string" ? a[prop] : prop(a);
			const bValue = typeof prop === "string" ? b[prop] : prop(b);
			if (typeof aValue === "number" && typeof bValue === "number") {
				return (isSortASC ? 1 : -1) * (aValue - bValue);
			}
			return (isSortASC ? 1 : -1) * String(aValue || "").localeCompare(String(bValue || ""));
		};
	};
}
